import { useAbility } from '@casl/react'
import { Space, Typography, message } from 'antd'
import React, { useContext, useEffect, useState } from 'react'
import { MeContext, UserPermissionFeature, useFeaturedBasketsQuery, BasketsListItem, Basket } from '../../../api'
import { AbilityContext } from '../../../api/components/Can'
import { BasketListItem } from '../../Baskets/components/BasketListItem'
import { HomeCarousel } from '../components/HomeCarousel'
import { HomeSection } from '../components/HomeSection'
import { useCreateSupportTicketMutation } from '../../../api/hooks/support-ticket'
import { CreateItemCard } from '../components/CreateItemCard'
import { useRebateOptimizerAccess } from '../../../common/hooks/useRebateOptimizerAccess'

const SUPPORT_TICKET_KEY = 'home_baskets_st_requested'

export const CategoriesSection: React.FC = () => {
  const { data: meData } = useContext(MeContext)
  const ability = useAbility(AbilityContext)
  const { hasRebateOptimizer } = useRebateOptimizerAccess()
  const hasPermission = ability.can('access', UserPermissionFeature.Baskets) || hasRebateOptimizer
  const userSupportTicketKey = `${SUPPORT_TICKET_KEY}_${meData?.me.user.id}`
  const [requested, setRequested] = useState(false)

  const { data, loading } = useFeaturedBasketsQuery({
    variables: {
      pagination: {
        limit: 9,
        offset: 0,
      },
    },
    skip: !hasPermission,
    fetchPolicy: 'network-only',
  })
  const [createSupportTicket, { loading: ticketLoading }] = useCreateSupportTicketMutation({
    onCompleted: () => {
      window.localStorage.setItem(userSupportTicketKey, Date.now().toString())
      setRequested(true)
      message.success(
        'We acknowledged your request. Customer Success team will contact you to provide more information.',
      )
    },
    onError: () => {
      message.error('Your request was failed. Please check your network or contact our support to help you fix it.')
    },
  })

  useEffect(() => {
    setRequested(!!window.localStorage.getItem(userSupportTicketKey))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <HomeSection
      title={
        <Space direction="horizontal">
          <Typography.Title level={4}>Market Baskets</Typography.Title>
          <Typography.Text style={{ fontSize: '20px' }}>(Top Quartile)</Typography.Text>
        </Space>
      }
      showAllLink={hasPermission && data?.featuredBaskets.length ? '/analytics/baskets' : undefined}>
      <HomeCarousel
        loading={loading}
        items={hasPermission ? data?.featuredBaskets || [] : []}
        renderItem={({ basket, featuredTag }) => {
          const basketListItem: BasketsListItem = {
            id: basket.id,
            name: basket.name,
            catalogId: basket.catalogId,
            archivedAt: basket.archivedAt,
            isCatalogBasket: basket.isCatalogBasket,
            isSearchAll: basket.isSearchAll,
            isUploadBasket: basket.isUploadBasket,
            isSmartBasket: basket.isSmartBasket,
            isContractBasket: basket.isContractBasket,
            type: basket.type,
            numItems: basket.insights12m?.numItems || 0,
            // numFacilities / facilityIds are not queried here and are unused by the home-carousel
            // card; only the Market Basket Management grid consumes them (work item #7206).
            numFacilities: 0,
            facilityIds: [],
            totalSpend: basket.insights12m?.totalSpend || 0,
            opportunity: calculateOpportunity(basket, 75) || 0,
            likelihoods: basket.likelihoods?.priceScoreLikelihoods,
          }
          return <BasketListItem basket={basketListItem} featuredTag={featuredTag} targetPercentile={75} />
        }}
        slidesToShow={3}
        extraItem={
          !hasPermission || !data?.featuredBaskets.length ? (
            <CreateItemCard
              title="Load Your Data"
              description={
                requested
                  ? 'We acknowledged your request. Customer Success team will contact you to provide more information.'
                  : 'Add spend data to Curvo to identify opportunities and track savings with flexible categorization.'
              }
              loading={ticketLoading}
              requested={requested}
              onClick={() => {
                createSupportTicket({ variables: { input: { subject: 'Request to add new Market Basket on CX' } } })
              }}
            />
          ) : null
        }
      />
    </HomeSection>
  )
}

const calculateOpportunity = (basket: Basket, targetPercentile: number) => {
  const insights = basket.insights12m || basket.insights
  if (insights?.projectedSpend && insights?.percentiles) {
    return insights.projectedSpend - insights.percentiles[100 - targetPercentile]
  }
  return undefined
}
